import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, type PublicStage } from '../api'
import { Loading } from '../Loading'
import { StageIcon } from '../StageIcon'

const KIND_LABEL: Record<string, string> = {
  player_list: '參賽名單',
  pairings: '對陣',
  round_results: '結果',
  standings: '積分榜',
  top_cut: 'Top Cut',
  final: '最終名次',
}

function stageTitle(s: PublicStage) {
  if (s.kind === 'pairings' && s.round != null) return { title: `第 ${s.round} 輪 對陣`, sub: `Round ${s.round}` }
  if (s.kind === 'round_results' && s.round != null) return { title: `第 ${s.round} 輪 結果`, sub: `Round ${s.round} Result` }
  if (s.kind === 'standings') return { title: '積分榜', sub: '' }
  if (s.kind === 'player_list') return { title: '參賽名單', sub: '' }
  return { title: `${KIND_LABEL[s.kind] ?? s.kind}${s.round != null ? ` · 第 ${s.round} 輪` : ''}`, sub: '' }
}

export function PublicEventPage() {
  const { slug } = useParams()
  const [data, setData] = useState<{
    event: { title: string; slug: string; updatedAt: string | number | Date }
    stages: PublicStage[]
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const storageKey = slug ? `ptcg.playerFilter:${slug}` : null
  const [filter, setFilter] = useState('')

  useEffect(() => {
    if (!slug) return
    void api
      .publicEvent(slug)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : '載入失敗'))
  }, [slug])

  useEffect(() => {
    if (!storageKey) return
    try {
      const v = localStorage.getItem(storageKey)
      if (v) setFilter(v)
    } catch {
      // ignore
    }
  }, [storageKey])

  useEffect(() => {
    if (!storageKey) return
    try {
      if (filter) localStorage.setItem(storageKey, filter)
      else localStorage.removeItem(storageKey)
    } catch {
      // ignore
    }
  }, [storageKey, filter])

  if (error) return <div className="card">找不到活動或尚未發佈內容：{error}</div>
  if (!data) return <Loading />

  const stages = [...data.stages].sort((a, b) => a.sortIndex - b.sortIndex)
  const nonRound = stages.filter((s) => s.kind !== 'pairings' && s.kind !== 'round_results')
  const rounds = new Map<number, { pairings?: PublicStage; results?: PublicStage }>()
  for (const s of stages) {
    if (s.round == null) continue
    if (s.kind !== 'pairings' && s.kind !== 'round_results') continue
    const r = rounds.get(s.round) ?? {}
    if (s.kind === 'pairings') r.pairings = s
    if (s.kind === 'round_results') r.results = s
    rounds.set(s.round, r)
  }
  const roundNums = Array.from(rounds.keys()).sort((a, b) => b - a)

  return (
    <div className="stack">
      <div className="card stack public-event-header">
        <h1 style={{ margin: 0 }}>{data.event.title}</h1>
      </div>

      <div className="card filter-bar">
        <div className="stack" style={{ gap: '0.5rem' }}>
          <div style={{ fontWeight: 700 }}>快速搜尋（會記住）</div>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <input
              className="input"
              placeholder="輸入姓名或 Player ID"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ flex: 1, maxWidth: 'none' }}
            />
            <button className="btn btn-sm" type="button" onClick={() => setFilter('')} disabled={!filter}>
              清除
            </button>
          </div>
        </div>
      </div>

      <div className="card stack">
        <h2 style={{ margin: 0 }}>輪次</h2>
        <div className="stack">
          {data.stages.length === 0 ? <div className="muted">尚未有已發佈階段</div> : null}
          {roundNums.map((n) => {
            const r = rounds.get(n)!
            return (
              <div key={n} className="card round-row">
                <div className="round-row-left">
                  <div style={{ fontWeight: 800 }}>{`第 ${n} 輪`}</div>
                  <div className="muted">{`Round ${n}`}</div>
                </div>
                <div className="round-row-actions">
                  {r.pairings ? (
                    <Link className="btn pill-link" style={{ minWidth: '6rem', textAlign: 'center', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }} to={`/e/${data.event.slug}/${r.pairings.id}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5"/><line x1="13" x2="19" y1="19" y2="13"/><line x1="16" x2="20" y1="16" y2="20"/><line x1="19" x2="21" y1="21" y2="19"/><polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5"/><line x1="5" x2="9" y1="14" y2="18"/><line x1="7" x2="4" y1="17" y2="20"/><line x1="3" x2="5" y1="19" y2="21"/></svg>
                      對陣
                    </Link>
                  ) : null}
                  {r.results ? (
                    <Link className="btn pill-link" style={{ minWidth: '6rem', textAlign: 'center', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }} to={`/e/${data.event.slug}/${r.results.id}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/></svg>
                      結果
                    </Link>
                  ) : null}
                </div>
              </div>
            )
          })}

          {nonRound.length > 0 ? <div className="muted">其他</div> : null}
          {nonRound.map((s) => {
            const t = stageTitle(s)
            return (
              <Link
                key={s.id}
                className="card"
                to={`/e/${data.event.slug}/${s.id}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <StageIcon kind={s.kind} size={18} />
                  {t.title}
                </div>
                {t.sub ? <div className="muted" style={{ marginTop: '0.25rem' }}>{t.sub}</div> : null}
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}
